<script setup>
import { humanReadableDuration, humanReadableTimestamp } from '@/composables/datetime'
import { getAge, getDeadAnimalIncidentColor } from '@/composables/incidents/incidentTimeHelper'
import { canTriggerDeadAnimals } from '@/composables/permissionHelper'
import DeadAnimalIncidentPlugin from '../CanvasPluginProviders/DeadAnimalIncidentPlugin.ts'
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import IncidentDisplay from '@/components/incidents/IncidentDisplay.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  pluginConfig: { type: Object, default: () => ({}) },
  plugin: { type: DeadAnimalIncidentPlugin, default: null },
  visualLabelingModeEnabled: { type: Boolean, default: false },
  expanded: { type: Boolean, default: false },
})
const emit = defineEmits([
  'zoomToSpecificPosition',
  'enableVisualLabelingMode',
  'disableVisualLabelingMode',
  'update:expanded',
  'update:pluginConfig',
  'toggleDeadAnimalOverlay',
])

const { mobile } = useDisplay()

const { t } = useI18n()

const availableMarkerTypes = [
  { value: 'circle', title: t('canvas.settings.overlay.dead_animals.marker_types.circle'), icon: 'mdi-circle-outline' },
  { value: 'box', title: t('canvas.settings.overlay.dead_animals.marker_types.box'), icon: 'mdi-square-outline' },
  { value: 'cross', title: t('canvas.settings.overlay.dead_animals.marker_types.cross'), icon: 'mdi-close' },
  { value: 'dot', title: t('canvas.settings.overlay.dead_animals.marker_types.dot'), icon: 'mdi-circle-small' },
]

const deadAnimalMarkerType = ref('circle')

const deadAnimalIncidents = computed(() => {
  if (!props.plugin?.reactiveProperties.incidents) return []
  const incidents = props.plugin.reactiveProperties.incidents.map(incident => {
    return {
      raw: incident,
      incidentId: incident.incidentId,
      cameraId: incident.identifier.cameraId,
      cameraName: incident.messagePayload.cameraName,
      age: getAge(incident),
      position: incident.position,
    }
  })
  return incidents.sort((a, b) => b.age - a.age)
})

const loading = computed(() => {
  return props.plugin?.reactiveProperties.loading
})

function toggleFalsePositive() {
  emit('update:pluginConfig', {
    ...props.pluginConfig,
    showFalsePositive: !props.pluginConfig.showFalsePositive,
  })
}

watch(deadAnimalMarkerType, () => {
  emit('update:pluginConfig', {
    ...props.pluginConfig,
    markerType: deadAnimalMarkerType.value,
  })
})
</script>

<template>
  <v-layout-card
    v-if="plugin && plugin.isEnabled && plugin.isEnabled() && !plugin.config.hideIncidentOverlay"
    :loading="loading"
    width="100%"
    :max-width="mobile ? '370px' : '420px'"
    max-height="80vh"
    :class="`px-4 py-3 position-absolute overflow-y-auto`"
    style="top: 84px; left: 16px"
  >
    <div class="d-flex flex-column ga-6">
      <div>
        <div class="d-flex flex-row justify-space-between align-center">
          <div class="d-flex align-center ga-2 text-truncate">
            <v-icon
              icon="mdi-skull-scan"
              size="large"
            />
            <v-card-title class="px-0 flex-shrink-1">
              {{ $t('canvas.dead_animal_incidents') }}
            </v-card-title>
          </div>
          <v-btn
            icon="mdi-close"
            size="small"
            variant="plain"
            class="rounded-circle"
            @click="$emit('toggleDeadAnimalOverlay', true)"
          />
        </div>
        <v-card-subtitle
          v-if="loading"
          class="px-0"
        >
          {{ $t('canvas.dead_animal_incidents_subtitle') }}
        </v-card-subtitle>
        <v-card-subtitle
          v-else
          class="px-0"
        >
          {{ $t('canvas.dead_animal_incidents_subtitle_with_count', { count: deadAnimalIncidents.length }) }}
        </v-card-subtitle>
      </div>

      <v-btn
        width="100%"
        variant="tonal"
        color="primary"
        prepend-icon="mdi-crosshairs-gps"
        :append-icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        @click="$emit('update:expanded', !expanded)"
      >
        {{ !expanded ? $t('canvas.show_dead_animal_Locations') : $t('canvas.hide_dead_animal_Locations') }}
      </v-btn>

      <div
        v-if="deadAnimalIncidents && expanded"
        class="d-flex flex-column ga-4"
      >
        <v-alert
          v-if="!mobile && pluginConfig?.showFalsePositive"
          type="warning"
          density="compact"
        >
          {{ $t('canvas.false_positive_dead_animal_incidents') }}
        </v-alert>

        <v-list
          v-if="deadAnimalIncidents && deadAnimalIncidents.length > 0"
          density="compact"
          max-height="300px"
          class="pa-0"
        >
          <v-list-item
            v-for="incident in deadAnimalIncidents"
            :key="incident.incidentId"
            variant="flat"
            lines="three"
            class="my-1 rounded-e-pill"
            :title="`${incident.cameraName} ${
              incident.raw.triggeredBy.entityType === 'account' ? '(' + $t('canvas.dead_animal_overlay.manual') + ')' : ''
            }`"
            :base-color="getDeadAnimalIncidentColor(incident.raw)"
          >
            <template #subtitle>
              <span>
                {{
                  $t('canvas.dead_animal_overlay.first_detection', {
                    timestamp: humanReadableTimestamp(incident.raw.firstEventTimestamp),
                  })
                }}
              </span>

              <br v-if="incident.raw.state !== 'deadAnimalExists'" />
              <span v-if="incident.raw.state !== 'deadAnimalExists'">
                {{
                  $t('canvas.dead_animal_overlay.last_detection', {
                    timestamp: humanReadableTimestamp(incident.raw.lastEventTimestamp),
                  })
                }}
              </span>

              <br v-if="incident.raw.state === 'deadAnimalExists'" />
              <span v-if="incident.raw.state === 'deadAnimalExists'">
                {{
                  $t('canvas.dead_animal_overlay.current_age', {
                    age: humanReadableDuration(incident.age, undefined, ['d', 'h'], true),
                  })
                }}
              </span>
              <br v-if="incident.raw.state !== 'deadAnimalExists'" />
              <span v-if="incident.raw.state !== 'deadAnimalExists'">
                {{
                  $t('canvas.dead_animal_overlay.total_age', {
                    age: humanReadableDuration(incident.age, undefined, ['d', 'h'], true),
                  })
                }}
              </span>
            </template>
            <template #append>
              <v-tooltip
                location="top"
                scroll-strategy="close"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-btn
                    v-bind="tooltipProps"
                    variant="tonal"
                    class="mx-1 my-3 rounded-pill"
                    size="small"
                    icon="mdi-crosshairs-gps"
                    :disabled="incident?.position?.w === 0 && incident?.position?.h === 0"
                    @click="
                      e => {
                        ;($emit('zoomToSpecificPosition', incident.position), e.stopPropagation())
                      }
                    "
                  />
                </template>
                <span>{{ $t('canvas.show_on_canvas') }}</span>
              </v-tooltip>
            </template>

            <v-dialog
              max-width="600px"
              activator="parent"
            >
              <IncidentDisplay
                :incident="incident.raw"
                class="w-100"
                hide-json-button
              />
            </v-dialog>
          </v-list-item>
        </v-list>

        <div
          v-if="deadAnimalIncidents && deadAnimalIncidents.length === 0"
          class="text-center text-subtitle-2 font-italic"
        >
          {{ $t('canvas.no_dead_animal_incidents') }}
        </div>

        <v-btn
          v-if="!pluginConfig?.showFalsePositive"
          width="100%"
          variant="tonal"
          append-icon="mdi-eye"
          prepend-icon="mdi-cube-scan"
          @click="toggleFalsePositive"
        >
          {{ $t('canvas.switch_to_false_positive_dead_animal_incidents') }}
        </v-btn>
        <v-btn
          v-else
          width="100%"
          color="warning"
          variant="tonal"
          append-icon="mdi-eye-off"
          prepend-icon="mdi-cube-scan"
          @click="toggleFalsePositive"
        >
          {{ $t('canvas.hide_false_positive_dead_animal_incidents') }}
        </v-btn>
      </div>

      <v-btn
        v-if="canTriggerDeadAnimals() && !visualLabelingModeEnabled"
        width="100%"
        color="primary"
        prepend-icon="mdi-marker"
        @click="$emit('enableVisualLabelingMode', 'deadAnimal')"
      >
        {{ $t('canvas.trigger_dead_animal') }}
      </v-btn>
      <v-btn
        v-if="visualLabelingModeEnabled"
        width="100%"
        variant="outlined"
        color="error"
        prepend-icon="mdi-marker-cancel"
        @click="$emit('disableVisualLabelingMode')"
      >
        {{ $t('canvas.disable_dead_animal_triggering') }}
      </v-btn>

      <div class="mb-2">
        <v-select
          v-model="deadAnimalMarkerType"
          variant="outlined"
          prepend-icon="mdi-shape-plus"
          :label="$t('canvas.settings.overlay.dead_animals.marker_type')"
          :items="availableMarkerTypes"
          item-value="value"
          :return-object="false"
          item-title="title"
          hide-details
        >
          <template #item="{ item, props: listProps }">
            <v-list-item
              v-bind="listProps"
              slim
            >
              <template #prepend>
                <v-icon :icon="item.raw.icon" />
              </template>
            </v-list-item>
          </template>
          <template #selection="{ item, props: selectProps }">
            <v-list-item
              v-bind="selectProps"
              density="compact"
              slim
            >
              <template #prepend>
                <v-icon :icon="item.raw.icon" />
              </template>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-select>
      </div>
    </div>
  </v-layout-card>
</template>

<style scoped></style>
